"use server";

import { createCalAdminClient } from "@/lib/cal/admin";
import { CalClient } from "@/lib/cal/client";
import { createLogger } from "@/lib/logger";
import { createMedplumClient } from "@/lib/medplum/backend";
import { Practice } from "@/models/practice/Practice";
import { Room } from "@/models/practice/Room";

const logger = createLogger("actions/cal/add-room-to-team");

interface AddRoomToTeamResponse {
  success: boolean;
  membershipId?: number;
  error?: string;
}

/**
 * Server action to add a room (treatment facility) to its practice's Cal.com team.
 * Rooms are added with ADMIN role so they can have their own event types managed.
 *
 * Note: Cal.com's EventTypeSettings atom requires ADMIN or OWNER role to view/edit
 * team event types. See: https://cal.com/docs/platform/atoms/event-type#event-type-settings
 *
 * If the room already has a team membership ID and team ID stored, this function will:
 * - Return success immediately if the team ID matches the requested practice team
 * - Log a warning and proceed to create a new membership if the team IDs don't match
 *
 * @param params - Room ID
 * @returns Success status and membership details or error
 */
export async function addRoomToTeamAction(params: {
  roomId: string;
}): Promise<AddRoomToTeamResponse> {
  try {
    const medplum = createMedplumClient({ audience: "user" });

    let room = await Room.getById(medplum, params.roomId);

    if (!room.calUserId) {
      logger.info("Room missing Cal.com user ID; provisioning calendar before team add", {
        roomId: params.roomId,
        practiceId: room.practiceId,
      });

      const calAdmin = createCalAdminClient();
      const ensuredRoomCalendar = await Room.ensureCalendar(medplum, calAdmin, room.id);
      room = ensuredRoomCalendar.room;
    }

    const calUserId = room.calUserId;

    if (!calUserId) {
      return {
        success: false,
        error: "Room does not have a Cal.com user ID",
      };
    }

    // Get the practice from the room's managingOrganization
    const practiceId = room.practiceId;
    if (!practiceId) {
      return {
        success: false,
        error: "Room does not have a practice assigned",
      };
    }

    const practice = await Practice.getById(medplum, practiceId);
    if (!practice.calTeamId) {
      return {
        success: false,
        error: "Practice does not have a Cal.com team configured",
      };
    }

    // Check if room already has a team membership
    if (room.calTeamMembershipId && room.calTeamId) {
      const teamIdMatch = room.calTeamId === parseInt(practice.calTeamId, 10);

      logger.info("Room already has Cal.com team membership", {
        roomId: params.roomId,
        practiceId,
        existingTeamId: room.calTeamId,
        existingMembershipId: room.calTeamMembershipId,
        requestedTeamId: practice.calTeamId,
        teamIdMatch,
      });

      if (teamIdMatch) {
        return {
          success: true,
          membershipId: room.calTeamMembershipId,
        };
      } else {
        logger.warn("Room has membership to different team", {
          roomId: params.roomId,
          existingTeamId: room.calTeamId,
          requestedTeamId: practice.calTeamId,
        });
      }
    }

    // Add room to the Cal.com team
    // Rooms get ADMIN role so they can have team event types assigned
    const calOrgClient = CalClient.forTeam();
    const response = await calOrgClient.createTeamMembership({
      teamId: parseInt(practice.calTeamId, 10),
      request: {
        userId: calUserId,
        accepted: true,
        role: "ADMIN",
      },
    });

    if (response.status !== "success" || !response.data) {
      logger.error("Failed to add room to Cal.com team", {
        roomId: params.roomId,
        practiceId,
        calTeamId: practice.calTeamId,
        calUserId,
      });
      return { success: false, error: "Failed to add room to team" };
    }

    // Store the membership ID and team ID on the room
    await Room.update(medplum, params.roomId, {
      calTeamId: response.data.teamId,
      calTeamMembershipId: response.data.id,
    });

    logger.info("Room added to Cal.com team successfully", {
      roomId: params.roomId,
      practiceId,
      calTeamId: practice.calTeamId,
      calUserId,
      membershipId: response.data.id,
      role: response.data.role,
    });

    // Note: Cal.com automatically creates child events for MANAGED event types
    // when a member is added to a team. This allows rooms to be booked using
    // the team's event types.

    return { success: true, membershipId: response.data.id };
  } catch (error) {
    logger.error("Error adding room to Cal.com team", { error, params });
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}
